<template>
  <v-layout class="dashboard-layout">
    <v-navigation-drawer
      v-if="isCompact"
      v-model="drawerOpen"
      :rail="false"
      temporary
      location="right"
      class="dashboard-drawer"
      width="240"
    >
      <div class="drawer-logo-block">
        <img :src="logo" alt="Lulu" class="drawer-logo" />
      </div>
      <v-list nav density="comfortable" color="primary">
        <v-list-item
          v-for="link in navLinks"
          :key="link.name"
          :to="{ name: link.name }"
          :prepend-icon="link.icon"
          :title="link.label"
          link
          rounded="lg"
        />
      </v-list>

      <!-- Sign out lives in the app bar on desktop; this drawer takes over
           whenever the bar is collapsed, so surface it (and the signed-in
           email, in full) here too. -->
      <template #append>
        <div v-if="userEmail" class="drawer-email">{{ userEmail }}</div>
        <v-list nav density="comfortable">
          <v-list-item
            class="drawer-signout"
            prepend-icon="mdi-logout"
            title="Sign out"
            link
            rounded="lg"
            @click="onSignOut"
          />
        </v-list>
      </template>
    </v-navigation-drawer>

    <v-app-bar
      :elevation="1"
      color="surface"
      class="dashboard-app-bar"
      :height="74"
    >
      <router-link
        class="appbar-logo-link"
        :to="{ name: 'dashboard-insights' }"
      >
        <img :src="logo" alt="Lulu Rewards" class="appbar-logo" />
      </router-link>

      <!-- When the bar is collapsed the nav and sign out move into the
           hamburger drawer, so the centre of the bar is free. Surface the
           business switcher there when the user belongs to more than one
           business. -->
      <div
        v-if="hasMultipleBusinesses && isCompact"
        class="appbar-switcher-mobile"
      >
        <BusinessSwitcher
          :business-ids="businessIds"
          :selected-id="selectedBusinessId"
          @change="onBusinessChange"
        />
      </div>

      <!-- Desktop cluster: switcher and nav sit against the logo so the whole
           right-hand side is left to the email and Sign out. It stays rendered
           while collapsed (hidden, out of flow) so its natural width remains
           measurable - see measureBar(). -->
      <div
        ref="desktopClusterEl"
        class="appbar-desktop"
        :class="{ 'appbar-desktop--collapsed': isCompact }"
      >
        <div v-if="hasMultipleBusinesses" class="appbar-switcher">
          <BusinessSwitcher
            :business-ids="businessIds"
            :selected-id="selectedBusinessId"
            @change="onBusinessChange"
          />
        </div>

        <nav class="appbar-nav" aria-label="Primary">
          <router-link
            :to="{ name: 'dashboard-insights' }"
            class="appbar-nav-link"
          >
            Insights
          </router-link>
          <router-link
            v-if="canManageEmployees"
            :to="{ name: 'dashboard-employees' }"
            class="appbar-nav-link"
          >
            Team
          </router-link>
          <router-link
            :to="{ name: 'dashboard-account' }"
            class="appbar-nav-link"
          >
            Account
          </router-link>
          <router-link
            :to="{ name: 'dashboard-settings' }"
            class="appbar-nav-link"
          >
            Settings
          </router-link>
        </nav>

        <div class="appbar-actions">
          <span class="appbar-email">{{ userEmail }}</span>

          <v-btn
            class="appbar-signout"
            variant="outlined"
            color="primary"
            density="comfortable"
            @click="onSignOut"
          >
            Sign out
          </v-btn>
        </div>
      </div>

      <v-app-bar-nav-icon
        v-if="isCompact"
        class="appbar-nav-icon"
        aria-label="Open navigation"
        @click="drawerOpen = !drawerOpen"
      />
    </v-app-bar>

    <v-main class="dashboard-main">
      <div class="dashboard-content">
        <router-view />
      </div>
    </v-main>
  </v-layout>
</template>

<script>
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
} from "vue";
import { useDisplay } from "vuetify";
import { useRouter } from "vue-router";
import BusinessSwitcher from "@/components/Molecules/BusinessSwitcher.vue";
import { authStore, signOut } from "@/stores/authStore.js";
import {
  businessContext,
  ensureSelectedBusiness,
  setSelectedBusiness,
} from "@/stores/businessContext.js";
import { getCurrentUserRole } from "@/firebase-business.js";
import { track } from "@/analytics.js";

export default {
  name: "DashboardShell",
  components: { BusinessSwitcher },
  setup() {
    const router = useRouter();
    const display = useDisplay();
    const logo = require("@/assets/logo.png");

    const drawerOpen = ref(false);
    const isWide = computed(() => display.mdAndUp.value);

    const allNavLinks = [
      { name: "dashboard-insights", label: "Insights", icon: "mdi-chart-line" },
      { name: "dashboard-employees", label: "Team", icon: "mdi-account-group" },
      { name: "dashboard-account", label: "Account", icon: "mdi-account-circle-outline" },
      { name: "dashboard-settings", label: "Settings", icon: "mdi-cog-outline" },
    ];

    const userEmail = computed(() => authStore.user?.email || "");
    const businessIds = computed(() => authStore.employerBusinessIds || []);
    const hasMultipleBusinesses = computed(() => businessIds.value.length >= 2);
    const selectedBusinessId = computed(() => businessContext.selectedBusinessId || "");

    // Role for the currently selected business. Default "Owner" while loading
    // so we don't briefly flash-hide the Employees link for admins/owners.
    const currentRole = ref("Owner");
    const canManageEmployees = computed(
      () => currentRole.value === "Owner" || currentRole.value === "Administrator"
    );
    const navLinks = computed(() =>
      allNavLinks.filter(
        (link) => link.name !== "dashboard-employees" || canManageEmployees.value
      )
    );

    const refreshCurrentRole = async () => {
      const id = selectedBusinessId.value;
      const email = userEmail.value;
      if (!id || !email) {
        currentRole.value = "Owner";
        return;
      }
      const resolved = await getCurrentUserRole(id, email);
      currentRole.value = resolved || "Employee";
    };

    watch(
      [selectedBusinessId, userEmail],
      () => {
        refreshCurrentRole();
      },
      { immediate: true }
    );

    // How much room the bar needs is account-dependent, not just viewport
    // dependent: the email is shown in full and the business switcher only
    // appears (with a business name of unknown length) for multi-business
    // users. So measure the real widths and fall back to the hamburger the
    // moment the clusters would overlap, rather than trusting a breakpoint.
    const desktopClusterEl = ref(null);
    const isCramped = ref(false);
    const isCompact = computed(() => !isWide.value || isCramped.value);
    // Keep in sync with the gaps in .appbar-desktop / .appbar-logo-link.
    const CLUSTER_GAP = 32;
    const LOGO_GAP = 32;

    const measureBar = () => {
      const cluster = desktopClusterEl.value;
      if (!cluster) return;
      const content = cluster.closest(".v-toolbar__content");
      if (!content) return;
      const children = Array.from(cluster.children);
      if (!children.length) return;

      const logoEl = content.querySelector(".appbar-logo-link");
      const style = window.getComputedStyle(content);
      const available =
        content.clientWidth -
        (parseFloat(style.paddingLeft) || 0) -
        (parseFloat(style.paddingRight) || 0) -
        (logoEl ? logoEl.offsetWidth : 0) -
        LOGO_GAP;
      const required =
        children.reduce((sum, el) => sum + el.offsetWidth, 0) +
        CLUSTER_GAP * (children.length - 1);

      isCramped.value = required > available;
    };

    const scheduleMeasure = () => {
      nextTick(() => measureBar());
    };

    let resizeObserver = null;

    onMounted(() => {
      measureBar();
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(measureBar);
        resizeObserver.observe(desktopClusterEl.value);
        const content = desktopClusterEl.value?.closest(".v-toolbar__content");
        if (content) resizeObserver.observe(content);
      }
      window.addEventListener("resize", measureBar);
      // Web fonts land after first paint and change the measured text widths.
      if (document.fonts?.ready) document.fonts.ready.then(scheduleMeasure);
    });

    onUnmounted(() => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", measureBar);
    });

    // Business names arrive asynchronously and widen the switcher; the email
    // and the role-gated Team link change the cluster width too.
    watch(
      [
        userEmail,
        hasMultipleBusinesses,
        canManageEmployees,
        () => businessContext.displayNames[selectedBusinessId.value],
      ],
      scheduleMeasure
    );

    // Don't leave the drawer flagged open once the bar expands again.
    watch(isCompact, (compact) => {
      if (!compact) drawerOpen.value = false;
    });

    // Provide a *getter* via computed so child injections stay reactive.
    provide("selectedBusinessId", selectedBusinessId);
    provide("currentUserEmail", userEmail);

    onMounted(() => {
      const email = authStore.user?.email || null;
      const ids = authStore.employerBusinessIds || [];
      ensureSelectedBusiness(email, ids);
    });

    // Keep selection valid when the employers list changes (e.g. M5 employee
    // edits remove the user from a business).
    watch(
      () => authStore.employerBusinessIds,
      (ids) => {
        const email = authStore.user?.email || null;
        ensureSelectedBusiness(email, ids || []);
      }
    );

    // Close mobile drawer on route change.
    const stop = router.afterEach(() => {
      if (isCompact.value) drawerOpen.value = false;
    });
    onUnmounted(() => stop && stop());

    const onBusinessChange = (id) => {
      setSelectedBusiness(id, authStore.user?.email || null);
      track("business_switcher_changed", { business_id: id });
    };

    const onSignOut = async () => {
      track("nav_clicked", { item: "sign_out", source: "dashboard" });
      await signOut();
      router.push("/business-signin");
    };

    return {
      logo,
      drawerOpen,
      isWide,
      isCompact,
      desktopClusterEl,
      navLinks,
      canManageEmployees,
      userEmail,
      businessIds,
      hasMultipleBusinesses,
      selectedBusinessId,
      onBusinessChange,
      onSignOut,
    };
  },
};
</script>

<style scoped>
.dashboard-layout {
  min-height: 100vh;
}

.dashboard-drawer {
  background: var(--lulu-surface) !important;
  border-right: 1px solid var(--lulu-border);
}

.drawer-logo-block {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px 12px 8px;
}

.drawer-logo {
  height: 36px;
  object-fit: contain;
}

.dashboard-app-bar {
  background: var(--lulu-surface) !important;
  border-bottom: 1px solid var(--lulu-border);
}

.dashboard-app-bar :deep(.v-toolbar__content) {
  padding-left: 2rem;
  padding-right: 40px;
}

.appbar-logo-link {
  display: inline-flex;
  align-items: center;
  margin-left: 0;
  text-decoration: none;
}

.appbar-logo {
  height: 50px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1));
  transition: transform 0.3s ease;
}

.appbar-logo:hover {
  transform: scale(1.05);
}

.appbar-desktop {
  display: flex;
  align-items: center;
  gap: 32px;
  flex: 1 1 auto;
  /* Shrink past the content so overflow is detectable instead of silently
     pushing the row wider. */
  min-width: 0;
  margin-left: 32px;
}

.appbar-desktop > * {
  flex: 0 0 auto;
}

/* Collapsed: out of flow and hidden (so it leaves the tab order too) but still
   laid out, which keeps its natural width measurable. */
.appbar-desktop--collapsed {
  position: absolute;
  top: 0;
  left: 0;
  margin-left: 0;
  visibility: hidden;
  pointer-events: none;
}

.appbar-switcher {
  display: flex;
  align-items: center;
}

.appbar-switcher-mobile {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
}

.appbar-nav {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.appbar-actions {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-left: auto;
}

/* The desktop cluster is out of flow while collapsed, so the hamburger has to
   claim the free space itself to stay right-aligned. */
.appbar-nav-icon {
  margin-left: auto;
}

.appbar-nav-link {
  color: var(--lulu-primary) !important;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.appbar-nav-link:hover {
  opacity: 0.75;
}

.appbar-nav-link.router-link-active {
  color: var(--lulu-primary) !important;
}

/* Shown in full - if that no longer fits, the whole bar collapses into the
   hamburger rather than truncating the address. */
.appbar-email {
  color: var(--lulu-primary);
  background: #e6f4ee;
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
  padding: 6px 14px;
  border-radius: 999px;
}

.drawer-email {
  padding: 8px 16px 4px;
  font-size: 13px;
  font-weight: 600;
  color: var(--lulu-primary);
  word-break: break-all;
}

.appbar-signout {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0 !important;
  border-radius: 999px !important;
  min-height: 36px !important;
  height: 36px !important;
  padding: 0 22px !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.appbar-signout :deep(.v-btn__content) {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.appbar-signout:hover {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
  transform: translateY(-1px);
}

.dashboard-main {
  background: var(--lulu-bg-gradient);
  min-height: 100vh;
}

.dashboard-content {
  padding: 24px;
  max-width: 1440px;
  margin: 0 auto;
}

@media (max-width: 768px) {
  .dashboard-content {
    padding: 16px;
  }
}

/* No email/nav breakpoints below this: the bar collapses on measurement
   instead - see measureBar(). */
</style>
